import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { 
  FileText, 
  Search, 
  Brain, 
  CheckCircle, 
  ArrowRight, 
  Building2, 
  Clock,
  Euro,
  Shield,
  Sparkles
} from 'lucide-react';

export default function GuideAppelsOffresPage() {
  return (
    <>
      <SEO 
        title="Guide Complet des Appels d'Offres Publics 2026"
        description="Comment trouver et gagner des appels d'offres publics en France. Guide complet : BOAMP, DECP, TED Europa, conseils experts, stratégies gagnantes."
        path="/guide-appels-offres"
      />
      <div className="min-h-screen bg-white">
        <Header />
        
        {/* Breadcrumb */}
        <Breadcrumb 
          items={[
            { label: 'Guide appels d\'offres' }
          ]} 
        />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-violet-50 to-white py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-medium mb-4">
              Guide complet 2026
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Comment Trouver et Gagner des Appels d'Offres Publics en France
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Le guide ultime pour comprendre les marchés publics, trouver les opportunités et remporter des contrats grâce à l'Intelligence Artificielle.
            </p>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Sommaire</h2>
          <nav className="grid md:grid-cols-2 gap-4">
            {[
              { id: 'quest-ce-quun-appel-doffre', title: "Qu'est-ce qu'un appel d'offres ?" },
              { id: 'types-marches-publics', title: 'Les types de marchés publics' },
              { id: 'ou-trouver-appels-offres', title: 'Où trouver les appels d\'offres' },
              { id: 'dce-dossier-consultation', title: 'Le DCE expliqué' },
              { id: 'repondre-appel-offre', title: 'Comment répondre à un appel d\'offres' },
              { id: 'ia-marches-publics', title: 'L\'IA au service des marchés publics' },
            ].map((item) => (
              <a 
                key={item.id}
                href={`#${item.id}`}
                className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-violet-300 hover:bg-violet-50 transition-colors"
              >
                <ArrowRight className="w-5 h-5 text-violet-600" />
                <span className="font-medium text-slate-700">{item.title}</span>
              </a>
            ))}
          </nav>
        </div>
      </section>

      {/* Content */}
      <article className="py-16">
        <div className="container mx-auto px-4 max-w-4xl prose prose-lg prose-slate">
          
          {/* Section 1 */}
          <section id="quest-ce-quun-appel-doffre" className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <FileText className="w-8 h-8 text-violet-600" />
              Qu'est-ce qu'un appel d'offres public ?
            </h2>
            <p className="text-slate-600 mb-6">
              Un <strong>appel d'offres public</strong> (ou marché public) est une procédure par laquelle une entité publique 
              (État, collectivités territoriales, hôpitaux, etc.) fait appel à des entreprises privées pour réaliser des travaux, 
              fournir des services ou livrer des fournitures.
            </p>
            <p className="text-slate-600 mb-6">
              En France, les marchés publics représentent environ <strong>200 milliards d'euros par an</strong>, 
              soit une opportunité considérable pour les entreprises de toutes tailles.
            </p>
            
            <Card className="bg-violet-50 border-violet-200 mb-8">
              <CardContent className="p-6">
                <h3 className="font-bold text-violet-900 mb-3">Chiffres clés des marchés publics en France</h3>
                <ul className="space-y-2 text-violet-800">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-violet-600" />
                    <span><strong>200 Md€</strong> de commande publique par an</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-violet-600" />
                    <span><strong>180 000+</strong> marchés publiés chaque année</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-violet-600" />
                    <span><strong>30%</strong> attribués aux PME</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </section>

          {/* Section 2 */}
          <section id="types-marches-publics" className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Building2 className="w-8 h-8 text-violet-600" />
              Les différents types de marchés publics
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">Travaux</h3>
                  <p className="text-sm text-slate-600">Construction, rénovation, génie civil, VRD...</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">Services</h3>
                  <p className="text-sm text-slate-600">Conseil, informatique, nettoyage, formation...</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Euro className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">Fournitures</h3>
                  <p className="text-sm text-slate-600">Équipements, matériels, consommables...</p>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-xl font-bold text-slate-900 mb-4">Les procédures selon le montant</h3>
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</span>
                <div>
                  <strong className="text-slate-900">MAPA (Marché à Procédure Adaptée)</strong>
                  <p className="text-slate-600">Pour les marchés inférieurs aux seuils européens. Procédure simplifiée, idéale pour les PME.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</span>
                <div>
                  <strong className="text-slate-900">Appel d'offres ouvert</strong>
                  <p className="text-slate-600">Tout candidat peut remettre une offre. C'est la procédure la plus courante.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 bg-violet-100 text-violet-700 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</span>
                <div>
                  <strong className="text-slate-900">Appel d'offres restreint</strong>
                  <p className="text-slate-600">Seuls les candidats présélectionnés peuvent soumettre une offre.</p>
                </div>
              </li>
            </ul>
          </section>

          {/* Section 3 */}
          <section id="ou-trouver-appels-offres" className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Search className="w-8 h-8 text-violet-600" />
              Où trouver les appels d'offres publics ?
            </h2>
            
            <p className="text-slate-600 mb-6">
              Les appels d'offres sont publiés sur plusieurs plateformes officielles. Voici les principales sources :
            </p>

            <div className="space-y-6 mb-8">
              <Card className="border-blue-200">
                <CardContent className="p-6">
                  <h3 className="font-bold text-blue-900 mb-2">BOAMP (Bulletin Officiel des Annonces des Marchés Publics)</h3>
                  <p className="text-slate-600 mb-2">La source officielle pour tous les marchés publics de l'État et des collectivités.</p>
                  <span className="text-sm text-blue-600">→ boamp.fr</span>
                </CardContent>
              </Card>
              
              <Card className="border-green-200">
                <CardContent className="p-6">
                  <h3 className="font-bold text-green-900 mb-2">DECP (Données Essentielles de la Commande Publique)</h3>
                  <p className="text-slate-600 mb-2">Base de données open data sur data.gouv.fr avec tous les marchés attribués.</p>
                  <span className="text-sm text-green-600">→ data.gouv.fr</span>
                </CardContent>
              </Card>
              
              <Card className="border-amber-200">
                <CardContent className="p-6">
                  <h3 className="font-bold text-amber-900 mb-2">TED (Tenders Electronic Daily)</h3>
                  <p className="text-slate-600 mb-2">Portail européen pour les marchés dépassant les seuils communautaires.</p>
                  <span className="text-sm text-amber-600">→ ted.europa.eu</span>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <Sparkles className="w-10 h-10 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Gagnez du temps avec l'IA</h3>
                    <p className="text-white/90 mb-4">
                      Au lieu de surveiller manuellement toutes ces sources, Mon DCE Express agrège automatiquement 
                      <strong> 6+ plateformes</strong> et utilise l'Intelligence Artificielle pour vous recommander 
                      les marchés les plus pertinents pour votre entreprise.
                    </p>
                    <Link to="/">
                      <Button className="bg-white text-violet-700 hover:bg-slate-100">
                        Essayer gratuitement
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Section 4 */}
          <section id="dce-dossier-consultation" className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <FileText className="w-8 h-8 text-violet-600" />
              Le DCE (Dossier de Consultation des Entreprises)
            </h2>
            
            <p className="text-slate-600 mb-6">
              Le <strong>DCE</strong> est l'ensemble des documents fournis par l'acheteur public pour permettre aux 
              entreprises de répondre à un appel d'offres. Il est essentiel de bien l'analyser avant de candidater.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-4">Composition d'un DCE</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                { title: 'RC', desc: 'Règlement de Consultation - Les règles du jeu' },
                { title: 'CCAP', desc: 'Clauses Administratives - Conditions contractuelles' },
                { title: 'CCTP', desc: 'Clauses Techniques - Le cahier des charges' },
                { title: 'BPU', desc: 'Bordereau des Prix - Grille tarifaire à compléter' },
                { title: 'DPGF', desc: 'Détail des Prix - Décomposition des coûts' },
                { title: 'Formulaires', desc: 'DC1, DC2, ATTRI1 - Documents administratifs' },
              ].map((doc) => (
                <Card key={doc.title} className="border-slate-200">
                  <CardContent className="p-4">
                    <span className="font-bold text-violet-600">{doc.title}</span>
                    <p className="text-sm text-slate-600">{doc.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-6">
                <h3 className="font-bold text-amber-900 mb-3 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Service DCE sous 24h
                </h3>
                <p className="text-amber-800 mb-4">
                  Avec Mon DCE Express, commandez n'importe quel DCE et recevez-le par email sous 24 heures. 
                  Plus besoin de naviguer sur plusieurs plateformes !
                </p>
                <p className="text-amber-700 font-medium">À partir de 2€ par DCE</p>
              </CardContent>
            </Card>
          </section>

          {/* Section 5 */}
          <section id="repondre-appel-offre" className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Shield className="w-8 h-8 text-violet-600" />
              Comment répondre à un appel d'offres
            </h2>
            
            <ol className="space-y-6">
              {[
                { step: 1, title: 'Analyser le DCE', desc: 'Lisez attentivement tous les documents, identifiez les critères de sélection et les délais.' },
                { step: 2, title: 'Vérifier votre éligibilité', desc: 'Assurez-vous de remplir toutes les conditions (certifications, références, capacité financière).' },
                { step: 3, title: 'Préparer le dossier administratif', desc: 'Rassemblez les documents obligatoires : DC1, DC2, attestations, assurances...' },
                { step: 4, title: 'Rédiger le mémoire technique', desc: 'Décrivez votre méthodologie, vos moyens et vos références. C\'est souvent décisif !' },
                { step: 5, title: 'Établir l\'offre de prix', desc: 'Complétez le BPU et la DPGF avec des prix cohérents et compétitifs.' },
                { step: 6, title: 'Déposer sur la plateforme', desc: 'Téléversez votre offre avant la date limite sur la plateforme de dématérialisation.' },
              ].map((item) => (
                <li key={item.step} className="flex items-start gap-4">
                  <span className="w-10 h-10 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    {item.step}
                  </span>
                  <div>
                    <h3 className="font-bold text-slate-900">{item.title}</h3>
                    <p className="text-slate-600">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* Section 6 */}
          <section id="ia-marches-publics" className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Brain className="w-8 h-8 text-violet-600" />
              L'Intelligence Artificielle au service des marchés publics
            </h2>
            
            <p className="text-slate-600 mb-6">
              L'<strong>Intelligence Artificielle</strong> révolutionne la façon dont les entreprises trouvent et 
              répondent aux appels d'offres. Voici comment l'IA peut vous aider :
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card className="border-violet-200 bg-violet-50">
                <CardContent className="p-6">
                  <h3 className="font-bold text-violet-900 mb-3">Veille automatique</h3>
                  <p className="text-violet-800">L'IA surveille en continu toutes les sources et vous alerte dès qu'un marché correspond à votre profil.</p>
                </CardContent>
              </Card>
              <Card className="border-violet-200 bg-violet-50">
                <CardContent className="p-6">
                  <h3 className="font-bold text-violet-900 mb-3">Matching intelligent</h3>
                  <p className="text-violet-800">Algorithmes de scoring pour identifier les marchés où vous avez le plus de chances de gagner.</p>
                </CardContent>
              </Card>
              <Card className="border-violet-200 bg-violet-50">
                <CardContent className="p-6">
                  <h3 className="font-bold text-violet-900 mb-3">Analyse des DCE</h3>
                  <p className="text-violet-800">Extraction automatique des informations clés et des critères de sélection.</p>
                </CardContent>
              </Card>
              <Card className="border-violet-200 bg-violet-50">
                <CardContent className="p-6">
                  <h3 className="font-bold text-violet-900 mb-3">Gain de temps</h3>
                  <p className="text-violet-800">Réduisez de 80% le temps passé à chercher et analyser les opportunités.</p>
                </CardContent>
              </Card>
            </div>
          </section>

        </div>
      </article>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à trouver vos prochains marchés publics ?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Essayez gratuitement Mon DCE Express et recevez 3 recommandations personnalisées sans inscription.
          </p>
          <Link to="/">
            <Button size="lg" className="bg-white text-violet-900 hover:bg-slate-100">
              <Sparkles className="w-5 h-5 mr-2" />
              Commencer gratuitement
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
    </>
  );
}
